<template>
  <MDBContainer class="p-0">
    <MDBCard>
      <MDBCardBody>
        <MDBCardTitle class="mb-5">{{
          $t("shop.orderSummary")
          }}</MDBCardTitle>
        <MDBCardText class="pb-0">
          <h6 class="mb-4">{{ $t("shop.selectedAddress") }}</h6>
          <div class="row mb-3">
            <div class="col-md-8">
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.streetHouseNr") }}</div>
                <div class="col-md-8">{{ address?.street || "—" }}</div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.postcode") }}:</div>
                <div class="col-md-8">{{ address?.postal_code || "—" }}</div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.location") }}</div>
                <div class="col-md-8">{{ address?.city || "—" }}</div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.country") }}</div>
                <div class="col-md-8">
                  {{ countryCode ? $t(`account.countries.${countryCode}`) : "—" }}
                </div>
              </div>
            </div>
          </div>
          <h6>{{ $t("shop.selectedProducts") }}</h6>
          <div class="my-3" v-if="cart?.items?.length > 0">
            <MDBTable>
              <thead>
                <tr>
                  <th scope="col">{{ $t("shop.product") }}</th>
                  <th scope="col">{{ $t("shop.attributes") }}</th>
                  <th scope="col">{{ $t("shop.quantity") }}</th>
                  <th scope="col">{{ $t("shop.cart.pointsSum") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in cart.items" :key="`product_${i}`">
                  <th scope="row">
                    <router-link :to="{
                      name: 'productDetails',
                      params: {
                        id: item.product.id,
                      },
                    }">
                      {{ item.product.name[currentLocale] }}
                    </router-link>
                  </th>
                  <th scope="row">
                    {{ showAttributes(item) }}
                  </th>
                  <th scope="row">
                    {{ item.quantity }}
                  </th>
                  <th scope="row">{{ item.points }}</th>
                </tr>
                <tr>
                  <th scope="row" colspan="2"></th>
                  <th class="border border-y-white" scope="row" colspan="2">
                    <p>
                      <strong>{{ cart.total }} </strong>
                      {{ $t("shop.pointsTotal") }}
                    </p>
                    <p>
                      <strong>{{ user.points?.avail_points - cart.total }} </strong>
                      {{ $t("shop.cartRemainPoints") }}
                    </p>
                  </th>
                </tr>
              </tbody>
            </MDBTable>
          </div>
        </MDBCardText>
      </MDBCardBody>
      <MDBCardFooter class="text-muted">
        <MDBBtn color="success" @click="createOrder()" :disabled="loading || pending || !address">{{
          $t("global.buttons.placeOrder") }}
        </MDBBtn>
        <MDBBtn color="primary" @click="back" :disabled="loading || pending">{{ $t("global.buttons.back") }}</MDBBtn>
      </MDBCardFooter>
    </MDBCard>
  </MDBContainer>

  <MDBModal v-model="showUrssafModal" tabindex="-1" centered size="lg">
    <UrssafDataModal :userId="user.plainId" v-if="showUrssafModal" @cancel="showUrssafModal = false"
      @submit="onUrssafSubmit" />
  </MDBModal>
</template>

<script setup>
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBCardFooter,
  MDBBtn,
  MDBTable,
  MDBModal,
} from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n'
import { computed, ref } from "vue";

import "vue3-toastify/dist/index.css";
import UrssafDataModal from "@/components/Shop/Order/UrssafDataModal.vue";
import { toast } from "vue3-toastify";
import { OrderService } from "../../../services/order.service";

const store = useStore();
const router = useRouter();
const { t } = useI18n()
store.dispatch("me");
store.dispatch("fetchUserShopCart");

const cart = computed(() => store.getters.getCart);
const addresses = computed(() => store.getters.getUserAddresses);
const pending = computed(() => store.getters.getPending);
const getAddressId = (item) => item?.plainId ?? item?.id ?? null;
const selectedAddressId = computed(() => store.getters.getSelectedAddress);
const address = computed(() => {
  const selected = (addresses.value || []).find(
    (item) => String(getAddressId(item)) === String(selectedAddressId.value)
  );

  return selected;
});
const countryCode = computed(() =>
  (address.value?.country ?? "").slice(0, 2).toLowerCase()
);

const user = computed(() => store.getters.getMe);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const loading = ref(false);
const back = () => {
  router.go(-1);
};

const isFrance = computed(() => currentLocale.value === 'fr-FR');

const urssafRequired = computed(() =>
  isFrance.value && user.value?.should_fill_report_data
);

const showUrssafModal = ref(false);
const urssafData = ref(null);

const showAttributes = (product) => {
  if (product.product_variant) {
    if (product.product_variant.title) {
      return product.product_variant.title[currentLocale.value]
    } else {
      return JSON.parse(product.product_variant.attribute_option)
        .map((item) => `${item.label}: ${item.option}`)
        .join(", ")
    }
  }
  return "-";
};

const createOrder = async () => {
  const addressId = getAddressId(address.value);
  if (!addressId) {
    toast(t("toast.incomplete_address"), {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.TOP_RIGHT,
    });
    return;
  }

  if (urssafRequired.value && !urssafData.value) {
    showUrssafModal.value = true;
    return;
  }

  loading.value = true
  const orderData = {
    token: cart.value.token,
    address_id: addressId,
  };

  const response = await store.dispatch("createOrder", orderData);

  if (response && response.status !== 200) {
    toast(response.data.message, {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.TOP_RIGHT,
    });
  } else {
    await router.push({ name: "orderComplete" });
  }
  loading.value = false
};

async function onUrssafSubmit(payload) {
  try {
    loading.value = true;
    await OrderService.createUrsaafPersonalData(payload)
  } catch (e) {
    toast("URSSAF submit failed", {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.TOP_RIGHT,
    });
    loading.value = false;
    return;
  }

  urssafData.value = payload;
  showUrssafModal.value = false;

  await createOrder();
  loading.value = false;
}

</script>

<style lang="scss" scoped>
.border-y-white {
  border-right-color: #fff !important;
  border-left-color: #fff !important;
}
</style>
